(() => {
  const instances = new Map();
  let swiperModule;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  // Hybrid touch computers keep native scrolling too. Never intercept a touch gesture.
  const desktopPointer = matchMedia('(min-width: 1001px) and (hover: hover) and (pointer: fine) and (not (any-pointer: coarse))');
  const initialize = () => document.querySelectorAll('[data-bw-home-port]').forEach(root => {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const { signal } = abort;
    const rows = [];
    let revealObserver;
    let proximityObserver;
    let carouselNear = false;
    let disposed = false;
    const reveals = root.querySelectorAll('.bwh-product-photo img,.bwh-goal>img,.bwh-feature>img,.bwh-education-card img');
    const reveal = () => {
      revealObserver?.disconnect();
      reveals.forEach(el => el.classList.remove('bwh-pending'));
      if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('bwh-pending');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: '0px' });
      reveals.forEach(el => {
        // Don't hide content already visible when enhancing the server rendered page.
        const rect = el.getBoundingClientRect();
        if (rect.top >= innerHeight || rect.left >= innerWidth) {
          el.classList.add('bwh-reveal', 'bwh-pending');
          revealObserver.observe(el);
        }
      });
    };
    root.querySelectorAll('[data-bwh-carousel]').forEach(viewport => {
      const track = viewport.querySelector('[data-bwh-track]');
      const slides = Array.from(track.children);
      const section = viewport.closest('section');
      const previous = section.querySelector('[data-bwh-prev]');
      const next = section.querySelector('[data-bwh-next]');
      const pagination = section.querySelector('[data-bwh-pagination]');
      let swiper;
      let points = [0];
      let buttons = [];
      let active = 0;
      let frame = 0;
      let generation = 0;
      const update = () => {
        frame = 0;
        const position = swiper ? -swiper.translate : viewport.scrollLeft;
        const end = points.at(-1) || 0;
        active = points.reduce((nearest, point, i) => Math.abs(position - point) < Math.abs(position - points[nearest]) ? i : nearest, 0);
        previous.disabled = position <= 2;
        next.disabled = position >= end - 2;
        buttons.forEach((button, i) => {
          if (i === active) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        pagination.hidden = points.length < 2;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const measure = () => {
        if (disposed) return;
        if (swiper) points = Array.from(swiper.snapGrid);
        else {
          const end = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
          points = [...new Set(slides.map(el => Math.min(end, Math.max(0, el.offsetLeft - slides[0].offsetLeft))))];
        }
        if (!points.length) points = [0];
        if (buttons.length !== points.length) {
          pagination.replaceChildren();
          buttons = points.map((_, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = String(i + 1).padStart(2, '0');
            button.setAttribute('aria-controls', viewport.id);
            button.addEventListener('click', () => moveTo(i), { signal });
            pagination.append(button);
            return button;
          });
        }
        update();
      };
      const moveTo = i => {
        const index = Math.max(0, Math.min(points.length - 1, i));
        if (swiper) swiper.slideTo(index);
        else viewport.scrollTo({ left: points[index], behavior: reduceMotion.matches ? 'instant' : 'smooth' });
      };
      const mode = async () => {
        const current = ++generation;
        const selected = swiper ? swiper.activeIndex : active;
        if (swiper) { swiper.destroy(true, true); swiper = undefined; }
        viewport.scrollLeft = 0;
        measure();
        viewport.scrollLeft = points[Math.min(selected, points.length - 1)];
        if (!carouselNear || !desktopPointer.matches || reduceMotion.matches) return;
        try {
          swiperModule ||= import(root.dataset.swiperUrl);
          const { default: Swiper } = await swiperModule;
          if (disposed || current !== generation || !desktopPointer.matches || reduceMotion.matches) return;
          viewport.scrollLeft = 0;
          swiper = new Swiper(viewport, {
            slidesPerView: viewport.classList.contains('bwh-carousel--goals') ? 4 : 3,
            spaceBetween: viewport.classList.contains('bwh-carousel--goals') ? 20 : 24,
            initialSlide: selected,
            speed: 450,
            loop: false,
            allowTouchMove: false,
            resizeObserver: true,
            freeMode: { enabled: true, momentum: false, sticky: false },
            mousewheel: { enabled: true, releaseOnEdges: true, forceToAxis: false, sensitivity: 1 },
            on: { progress: schedule, slideChange: schedule, resize: () => requestAnimationFrame(measure) }
          });
          measure();
        } catch {
          // A failed enhancement must leave every card and control usable natively.
          swiperModule = undefined;
          measure();
        }
      };
      previous.addEventListener('click', () => moveTo(active - 1), { signal });
      next.addEventListener('click', () => moveTo(active + 1), { signal });
      viewport.addEventListener('scroll', schedule, { passive: true, signal });
      viewport.addEventListener('wheel', event => {
        // Swiper must not consume browser zoom or modified wheel shortcuts.
        if (swiper && (event.ctrlKey || event.metaKey || event.altKey)) event.stopImmediatePropagation();
      }, { capture: true, passive: true, signal });
      viewport.addEventListener('keydown', event => {
        if (event.target !== viewport) return;
        const destinations = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: points.length - 1 };
        if (Object.hasOwn(destinations, event.key)) { event.preventDefault(); moveTo(destinations[event.key]); }
      }, { signal });
      viewport.addEventListener('focusin', event => {
        if (!swiper) return;
        const slide = event.target.closest('.swiper-slide');
        const i = slides.indexOf(slide);
        if (i < 0) return;
        const start = swiper.slidesGrid[i];
        const position = -swiper.translate;
        if (start < position - 2 || start + swiper.slidesSizesGrid[i] > position + swiper.width + 2) swiper.slideTo(i);
      }, { signal });
      const observer = new ResizeObserver(() => { if (!swiper) measure(); });
      observer.observe(viewport);
      rows.push({ mode, dispose: () => { generation++; swiper?.destroy(true, true); observer.disconnect(); cancelAnimationFrame(frame); } });
      mode();
    });
    // Keep both rows native until the first row approaches the viewport.
    const firstCarousel = root.querySelector('[data-bwh-carousel]');
    if (firstCarousel && 'IntersectionObserver' in window) {
      proximityObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting) || disposed) return;
        carouselNear = true;
        proximityObserver.disconnect();
        rows.forEach(row => row.mode());
      }, { rootMargin: '400px 0px', threshold: 0 });
      proximityObserver.observe(firstCarousel);
    }
    const changeMode = () => { rows.forEach(row => row.mode()); reveal(); };
    desktopPointer.addEventListener('change', changeMode, { signal });
    reduceMotion.addEventListener('change', changeMode, { signal });
    reveal();
    instances.set(root, () => {
      disposed = true;
      abort.abort();
      revealObserver?.disconnect();
      proximityObserver?.disconnect();
      rows.forEach(row => row.dispose());
      instances.delete(root);
    });
  });
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => { if (event.target.contains(root)) dispose(); });
  });
})();
